import invariant from 'invariant'
import {first, isNil} from 'lodash'
import type {ZodMiniType} from 'zod/mini'
import * as z from 'zod/mini'


type ZodMiniRawShape = {
  readonly [key: string]: z.ZodMiniType
}

export const uuid = () => z.uuid().register(z.globalRegistry, {example: 'e917c393-fd85-41e5-a6b2-60348515b127'})

export const id = () => z.pipe(
  z.pipe(z.unknown(), z.transform((value) => value && Number(value))),
  z.number().check(z.int(), z.gte(1)),
).register(z.globalRegistry, {example: 1})

export const timestamp = () => z.coerce.number().check(z.int(), z.gte(1))
  .register(z.globalRegistry, {example: 1561161600})

export const standardString = (example?: string) => z.string().check(z.minLength(1), z.maxLength(255))
  .register(z.globalRegistry, {example})

export const positiveInteger = (opts?: {example?: number, max?: number, min?: number, defaultValue?: number}) => z.pipe(
  z.pipe(z.unknown(), z.transform((value) => !isNil(value) ? Number(value) : opts?.defaultValue)),
  z.number().check(z.int(), z.positive(), z.lte(opts?.max || Infinity), z.gte(opts?.min || 1))
).register(z.globalRegistry, {example: opts?.example})

export const number = (example?: number) => z.pipe(
  z.pipe(z.unknown(), z.transform((value) => value && Number(value))),
  z.number()
).register(z.globalRegistry, {example})

export const nonNegativeInteger = (opts?: {example?: number, max?: number, defaultValue?: number}) => z.pipe(
  z.pipe(z.unknown(), z.transform((value) => !isNil(value) ? Number(value) : opts?.defaultValue)),
  z.number().check(z.int(), z.nonnegative(), z.lte(opts?.max || Infinity))
).register(z.globalRegistry, {example: opts?.example})

export const boolean = (example: boolean = true) => z.pipe(
  z.pipe(z.unknown(), z.transform((value) => {
    if (value === 'true') return true
    if (value === 'false') return false
    return value
  })),
  z.boolean()
).register(z.globalRegistry, {example})

export const undefinedOptional = () => z.optional(z.undefined())

export const optionalObject = <T extends ZodMiniRawShape>(keys: T) => (
  z.partial(z.object(keys))
)

export const collection = <T extends ZodMiniRawShape>(schema: T) => z.array(z.object(schema))

export const oneOrMany = <T extends ZodMiniType>(schema: T) => z.union([z.array(schema), schema])

export const createEnum = <U extends string>(values: Readonly<U[]>) => {
  invariant(values.length > 0, 'Enum must have at least one possible value')

  const nonEmptyValues = [values[0], ...values.slice(1)] as const
  return z.enum(nonEmptyValues).register(z.globalRegistry, {example: first(values)})
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const jsonLiteralSchema = z.union([z.string(), z.number(), z.boolean(), z.null()])
type JSONLiteral = z.infer<typeof jsonLiteralSchema>
export type JSONObject = { [key: string]: JSONValue | undefined }
export type JSONValue = JSONLiteral | JSONObject | JSONValue[]

type AllKeys<T> = T extends unknown ? keyof T : never
type PickType<T, K extends AllKeys<T>> = T extends { [k in K]?: unknown } ? T[K] : undefined

/**
 * Merge union of objects into a single object with unions of values for all keys.
 *
 * @example
 * type Foo = {a: string, b: number} | {a: number, b: string} | {a: string, c: number}
 * type Bar = MergeFields<Foo> // { a: string | number; b: string | number | undefined; c: number | undefined; }
 *
 * @see https://dev.to/lucianbc/union-type-merging-in-typescript-9al
 */
export type MergeFields<T extends object> = { [k in AllKeys<T>]: PickType<T, k>; }
